import { abortRequestAfter } from "./abort-request-after";
import { getEnv } from "./envManager";
import { isHttpError } from "@common/utils/http-error";
import type { SnapshotStatus } from "./redline-failure";
import type { RedlineFailureAttempt } from "./redline-failure-attempt";
import type { RedlineFailureSnapshotRequest } from "./redline-wire";
import { encodeFailureAttemptForWire } from "./redline-wire";
import { safeFetch } from "./safe-fetch";

export const REDLINE_FAILURE_SNAPSHOT_PATH = "/redline/failure-snapshot";

export const SNAPSHOT_UPLOAD_TIMEOUT_MS = 60_000;

export const STATUSES_WORTH_RESENDING_THE_DOCUMENT_FOR = [408, 429, 503, 504];

export interface UploadRedlineFailureSnapshotArgs {
    authHeader: string;
    snapshotId: string;
    documentBase64: string | null;
    attempt: RedlineFailureAttempt;
}

export async function uploadRedlineFailureSnapshot({
    authHeader,
    snapshotId,
    documentBase64,
    attempt,
}: UploadRedlineFailureSnapshotArgs): Promise<Extract<SnapshotStatus, "stored" | "too_large">> {
    const request: RedlineFailureSnapshotRequest = {
        snapshot_id: snapshotId,
        document_base64: documentBase64,
        attempt: encodeFailureAttemptForWire(attempt),
    };

    try {
        await abortRequestAfter(SNAPSHOT_UPLOAD_TIMEOUT_MS, signal =>
            safeFetch(`${getEnv("API_BASE_URL")}${REDLINE_FAILURE_SNAPSHOT_PATH}`, {
                method: "POST",
                headers: {
                    Authorization: authHeader,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(request),
                signal,
            }, { retryStatuses: STATUSES_WORTH_RESENDING_THE_DOCUMENT_FOR }));
        return "stored";
    } catch (error) {
        if (isHttpError(error) && error.status === 413) {
            return "too_large";
        }
        throw error;
    }
}
